import React, { createContext, useState, useContext, useEffect } from 'react';
import apiService from '../services/apiService';
import toast from 'react-hot-toast';
import { buildOrganizationSlug } from '../utils/organizationSlug';

const OrganizationContext = createContext();

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
};

export const OrganizationProvider = ({ children }) => {
  const [organizations, setOrganizations] = useState([]);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper function to clear context
  const clearContext = () => {
    setOrganizations([]);
    setCurrentOrganization(null);
    setSubscription(null);
    localStorage.removeItem('currentOrganizationId');
    setLoading(false);
  };

  // Load organizations on mount (Login stores both `token` and `authToken` after saveSession)
  useEffect(() => {
    const token =
      localStorage.getItem('authToken') || localStorage.getItem('token');
    if (token) {
      loadOrganizations();
    } else {
      clearContext();
    }
  }, []);

  // Load subscription when organization changes
  useEffect(() => {
    if (currentOrganization) {
      loadSubscription(currentOrganization.id);
      // Save to localStorage
      localStorage.setItem('currentOrganizationId', currentOrganization.id);
    }
  }, [currentOrganization]);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      // Clear cached organization ID to force reload
      const savedOrgId = localStorage.getItem('currentOrganizationId');
      
      const orgs = await apiService.getOrganizations();
      setOrganizations(orgs);

      // Set current organization from localStorage or first organization
      // But verify that the saved org exists in the new list
      if (
        savedOrgId &&
        orgs.find((org) => String(org.id) === String(savedOrgId))
      ) {
        setCurrentOrganization(
          orgs.find((org) => String(org.id) === String(savedOrgId))
        );
      } else {
        // Clear invalid cached org ID and use first org
        localStorage.removeItem('currentOrganizationId');
        if (orgs.length > 0) {
          setCurrentOrganization(orgs[0]);
        }
      }
    } catch (error) {
      console.error('Failed to load organizations:', error);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscription = async (orgId) => {
    try {
      const sub = await apiService.getSubscription(orgId);
      setSubscription(sub);
    } catch (error) {
      console.error('Failed to load subscription:', error);
      // Don't show error toast for missing subscription - it's expected for new orgs
      setSubscription(null);
    }
  };

  const createOrganization = async (data) => {
    try {
      const payload = {
        ...data,
        slug: data?.slug?.trim() || buildOrganizationSlug(data?.name),
      };
      const newOrg = await apiService.createOrganization(payload);
      setOrganizations((prev) => [...prev, newOrg]);
      setCurrentOrganization(newOrg);
      toast.success('Organization created successfully');
      return newOrg;
    } catch (error) {
      toast.error(error.message);
      throw error;
    }
  };

  const updateOrganization = async (orgId, data) => {
    if (!orgId) {
      toast.error('No organization selected');
      return null;
    }

    try {
      const updatedOrg = await apiService.updateOrganization(orgId, data);
      setOrganizations((prev) =>
        prev.map((org) => (String(org.id) === String(orgId) ? updatedOrg : org)),
      );
      setCurrentOrganization((prev) =>
        prev && String(prev.id) === String(orgId) ? updatedOrg : prev,
      );
      toast.success('Organization updated successfully');
      return updatedOrg;
    } catch (error) {
      toast.error(error.message);
      throw error;
    }
  };

  const switchOrganization = (orgId) => {
    const org = organizations.find(
      (o) => String(o.id) === String(orgId),
    );
    if (org) {
      setCurrentOrganization(org);
      toast.success(`Switched to ${org.name}`);
    }
  };

  const updateSubscription = async (planId) => {
    if (!currentOrganization) {
      toast.error('No organization selected');
      return;
    }

    try {
      const newSub = await apiService.updateSubscription(currentOrganization.id, planId);
      setSubscription(newSub);
      toast.success('Subscription updated successfully');
      return newSub;
    } catch (error) {
      toast.error(error.message);
      throw error;
    }
  };

  const refreshOrganizations = () => {
    return loadOrganizations();
  };

  const refreshSubscription = () => {
    if (currentOrganization) {
      return loadSubscription(currentOrganization.id);
    }
  };

  const clearOrganizationContext = () => {
    clearContext();
  };

  const reloadOrganizations = async () => {
    // Force reload organizations (used after login)
    localStorage.removeItem('currentOrganizationId');
    return loadOrganizations();
  };

  const value = {
    organizations,
    currentOrganization,
    subscription,
    loading,
    createOrganization,
    updateOrganization,
    switchOrganization,
    updateSubscription,
    refreshOrganizations,
    refreshSubscription,
    clearOrganizationContext,
    reloadOrganizations
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};
